import { randomDelay } from "@/lib/utils/sleep";

export type SubscriptionStatus =
  | "none"
  | "trialing"
  | "active"
  | "past_due"
  | "cancelled"
  | "expired";

export type SubscriptionOrigin = "none" | "paid" | "free_trial" | "free_premium" | "courtesy";

export type BillingCycle = "monthly" | "annual";

export type PaymentMethod = "credit_card" | "pix" | "boleto";

export type PaymentStatus =
  | "pending"
  | "paid"
  | "failed"
  | "refunded"
  | "chargeback";

export type DerivedPaymentStatus = PaymentStatus | "overdue" | "scheduled";

export type PaymentStatusFilter = "all" | "scheduled" | "overdue" | "paid" | "refunded";

export interface MySubscriptionPlan {
  id: string;
  code: string;
  name: string;
  monthlyPriceCents: number;
  currency: "BRL";
  billingCycle: BillingCycle;
}

export interface NextInvoice {
  dueDate: string;
  amountCents: number;
  method: PaymentMethod | null;
}

export interface DefaultPaymentMethod {
  kind: PaymentMethod | null;
  cardBrand?: string;
  cardLast4?: string;
}

export interface SubscriptionGrant {
  reason: string;
}

export interface MySubscription {
  subscriptionId: string;
  status: SubscriptionStatus;
  plan: MySubscriptionPlan;
  origin: SubscriptionOrigin;
  trialEndsAt: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  nextInvoice: NextInvoice | null;
  defaultPaymentMethod: DefaultPaymentMethod | null;
  grantedBy: SubscriptionGrant | null;
}

export interface BoletoDetails {
  barcode: string;
  pdfUrl: string;
}

export interface PixDetails {
  qrCode: string;
  copyPaste: string;
  expiresAt: string;
}

export interface CardDetails {
  brand: string;
  last4: string;
}

export interface MyPayment {
  id: string;
  competence: string;
  amountCents: number;
  currency: "BRL";
  method: PaymentMethod;
  status: PaymentStatus;
  dueDate: string;
  paidAt: string | null;
  gateway: "pagarme" | "iugu" | "stripe" | null;
  receiptUrl: string | null;
  boleto?: BoletoDetails;
  pix?: PixDetails;
  card?: CardDetails;
}

export interface ListMyPaymentsParams {
  status?: PaymentStatusFilter;
  page?: number;
  pageSize?: number;
}

export interface PagedPayments {
  items: MyPayment[];
  total: number;
  page: number;
  pageSize: number;
}

const now = new Date("2026-05-24T12:00:00Z");
const daysFromNow = (d: number) =>
  new Date(now.getTime() + d * 86_400_000).toISOString();
const dateFromNow = (d: number) => {
  const dt = new Date(now.getTime() + d * 86_400_000);
  return dt.toISOString().slice(0, 10);
};
const competenceFromNow = (months: number) => {
  const dt = new Date(now);
  dt.setUTCMonth(dt.getUTCMonth() + months);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}`;
};

type Scenario =
  | "paid_active"
  | "trial"
  | "premium"
  | "courtesy"
  | "past_due"
  | "cancelled"
  | "expired";

const PLAN_PRO: MySubscriptionPlan = {
  id: "plan_pro_01",
  code: "pro",
  name: "Pro",
  monthlyPriceCents: 14900,
  currency: "BRL",
  billingCycle: "monthly",
};

const PLAN_STARTER: MySubscriptionPlan = {
  id: "plan_starter_01",
  code: "starter",
  name: "Starter",
  monthlyPriceCents: 4900,
  currency: "BRL",
  billingCycle: "monthly",
};

const subscriptionByScenario: Record<Scenario, MySubscription> = {
  paid_active: {
    subscriptionId: "sub_01",
    status: "active",
    plan: PLAN_PRO,
    origin: "paid",
    trialEndsAt: null,
    currentPeriodStart: daysFromNow(-23),
    currentPeriodEnd: daysFromNow(7),
    nextInvoice: {
      dueDate: dateFromNow(8),
      amountCents: PLAN_PRO.monthlyPriceCents,
      method: "pix",
    },
    defaultPaymentMethod: { kind: "credit_card", cardBrand: "visa", cardLast4: "4242" },
    grantedBy: null,
  },
  trial: {
    subscriptionId: "sub_02",
    status: "trialing",
    plan: PLAN_PRO,
    origin: "free_trial",
    trialEndsAt: daysFromNow(10),
    currentPeriodStart: daysFromNow(-4),
    currentPeriodEnd: daysFromNow(10),
    nextInvoice: null,
    defaultPaymentMethod: null,
    grantedBy: { reason: "Trial estendido — piloto comercial Q2/2026" },
  },
  premium: {
    subscriptionId: "sub_03",
    status: "active",
    plan: PLAN_PRO,
    origin: "free_premium",
    trialEndsAt: null,
    currentPeriodStart: daysFromNow(-60),
    currentPeriodEnd: daysFromNow(120),
    nextInvoice: null,
    defaultPaymentMethod: null,
    grantedBy: { reason: "Parceria estratégica — sem prazo definido" },
  },
  courtesy: {
    subscriptionId: "sub_04",
    status: "active",
    plan: PLAN_PRO,
    origin: "courtesy",
    trialEndsAt: null,
    currentPeriodStart: daysFromNow(-12),
    currentPeriodEnd: daysFromNow(60),
    nextInvoice: null,
    defaultPaymentMethod: null,
    grantedBy: { reason: "Compensação por incidente #INC-4521" },
  },
  past_due: {
    subscriptionId: "sub_05",
    status: "past_due",
    plan: PLAN_STARTER,
    origin: "paid",
    trialEndsAt: null,
    currentPeriodStart: daysFromNow(-35),
    currentPeriodEnd: daysFromNow(-5),
    nextInvoice: {
      dueDate: dateFromNow(-3),
      amountCents: PLAN_STARTER.monthlyPriceCents,
      method: "boleto",
    },
    defaultPaymentMethod: { kind: "boleto" },
    grantedBy: null,
  },
  cancelled: {
    subscriptionId: "sub_06",
    status: "cancelled",
    plan: PLAN_PRO,
    origin: "paid",
    trialEndsAt: null,
    currentPeriodStart: daysFromNow(-90),
    currentPeriodEnd: daysFromNow(-30),
    nextInvoice: null,
    defaultPaymentMethod: { kind: "credit_card", cardBrand: "mastercard", cardLast4: "5566" },
    grantedBy: null,
  },
  expired: {
    subscriptionId: "sub_07",
    status: "expired",
    plan: PLAN_PRO,
    origin: "free_trial",
    trialEndsAt: daysFromNow(-2),
    currentPeriodStart: daysFromNow(-16),
    currentPeriodEnd: daysFromNow(-2),
    nextInvoice: null,
    defaultPaymentMethod: null,
    grantedBy: { reason: "Trial de 14 dias — não convertido" },
  },
};

const paymentsByScenario: Record<Scenario, MyPayment[]> = {
  paid_active: [
    {
      id: "pay_01",
      competence: competenceFromNow(-4),
      amountCents: PLAN_PRO.monthlyPriceCents,
      currency: "BRL",
      method: "credit_card",
      status: "paid",
      dueDate: dateFromNow(-114),
      paidAt: daysFromNow(-114),
      gateway: "pagarme",
      receiptUrl: "https://example.com/receipts/pay_01.pdf",
      card: { brand: "visa", last4: "4242" },
    },
    {
      id: "pay_02",
      competence: competenceFromNow(-3),
      amountCents: PLAN_PRO.monthlyPriceCents,
      currency: "BRL",
      method: "credit_card",
      status: "paid",
      dueDate: dateFromNow(-84),
      paidAt: daysFromNow(-84),
      gateway: "pagarme",
      receiptUrl: "https://example.com/receipts/pay_02.pdf",
      card: { brand: "visa", last4: "4242" },
    },
    {
      id: "pay_03",
      competence: competenceFromNow(-2),
      amountCents: PLAN_PRO.monthlyPriceCents,
      currency: "BRL",
      method: "pix",
      status: "paid",
      dueDate: dateFromNow(-54),
      paidAt: daysFromNow(-55),
      gateway: "iugu",
      receiptUrl: "https://example.com/receipts/pay_03.pdf",
    },
    {
      id: "pay_04",
      competence: competenceFromNow(-1),
      amountCents: PLAN_PRO.monthlyPriceCents,
      currency: "BRL",
      method: "pix",
      status: "paid",
      dueDate: dateFromNow(-24),
      paidAt: daysFromNow(-23),
      gateway: "iugu",
      receiptUrl: "https://example.com/receipts/pay_04.pdf",
    },
    {
      id: "pay_05",
      competence: competenceFromNow(-1),
      amountCents: PLAN_PRO.monthlyPriceCents,
      currency: "BRL",
      method: "boleto",
      status: "pending",
      dueDate: dateFromNow(-3),
      paidAt: null,
      gateway: "iugu",
      receiptUrl: null,
      boleto: {
        barcode: "23793.39101 60082.013579 21001.456704 6 98290000014900",
        pdfUrl: "https://example.com/boletos/pay_05.pdf",
      },
    },
    {
      id: "pay_06",
      competence: competenceFromNow(0),
      amountCents: PLAN_PRO.monthlyPriceCents,
      currency: "BRL",
      method: "pix",
      status: "pending",
      dueDate: dateFromNow(8),
      paidAt: null,
      gateway: "iugu",
      receiptUrl: null,
      pix: {
        qrCode: "00020126360014BR.GOV.BCB.PIX0114+5511999990000520400005303986540514.905802BR5913IMOBGO LTDA6009SAO PAULO62290525IMOBGO-SUB-PAY06-2026-053063044C9C",
        copyPaste:
          "00020126360014BR.GOV.BCB.PIX0114+5511999990000520400005303986540514.905802BR5913IMOBGO LTDA6009SAO PAULO62290525IMOBGO-SUB-PAY06-2026-053063044C9C",
        expiresAt: daysFromNow(8),
      },
    },
  ],
  trial: [],
  premium: [],
  courtesy: [],
  past_due: [
    {
      id: "pay_pd_01",
      competence: competenceFromNow(-2),
      amountCents: PLAN_STARTER.monthlyPriceCents,
      currency: "BRL",
      method: "boleto",
      status: "pending",
      dueDate: dateFromNow(-33),
      paidAt: null,
      gateway: "iugu",
      receiptUrl: null,
      boleto: {
        barcode: "23793.39101 60082.013579 21001.456704 6 98290000004900",
        pdfUrl: "https://example.com/boletos/pay_pd_01.pdf",
      },
    },
    {
      id: "pay_pd_02",
      competence: competenceFromNow(-1),
      amountCents: PLAN_STARTER.monthlyPriceCents,
      currency: "BRL",
      method: "boleto",
      status: "pending",
      dueDate: dateFromNow(-3),
      paidAt: null,
      gateway: "iugu",
      receiptUrl: null,
      boleto: {
        barcode: "23793.39101 60082.013579 21001.456704 6 98290000004900",
        pdfUrl: "https://example.com/boletos/pay_pd_02.pdf",
      },
    },
  ],
  cancelled: [
    {
      id: "pay_cn_01",
      competence: competenceFromNow(-2),
      amountCents: PLAN_PRO.monthlyPriceCents,
      currency: "BRL",
      method: "credit_card",
      status: "paid",
      dueDate: dateFromNow(-54),
      paidAt: daysFromNow(-54),
      gateway: "stripe",
      receiptUrl: "https://example.com/receipts/pay_cn_01.pdf",
      card: { brand: "mastercard", last4: "5566" },
    },
    {
      id: "pay_cn_02",
      competence: competenceFromNow(-1),
      amountCents: PLAN_PRO.monthlyPriceCents,
      currency: "BRL",
      method: "credit_card",
      status: "chargeback",
      dueDate: dateFromNow(-24),
      paidAt: daysFromNow(-23),
      gateway: "stripe",
      receiptUrl: null,
      card: { brand: "mastercard", last4: "5566" },
    },
  ],
  expired: [],
};

let activeScenario: Scenario = "paid_active";

export function setMockScenario(scenario: Scenario): void {
  activeScenario = scenario;
}

export function getMockScenario(): Scenario {
  return activeScenario;
}

export const MOCK_SCENARIOS: readonly Scenario[] = [
  "paid_active",
  "trial",
  "premium",
  "courtesy",
  "past_due",
  "cancelled",
  "expired",
] as const;

export function deriveStatus(payment: MyPayment, reference: Date = new Date()): DerivedPaymentStatus {
  if (payment.status !== "pending") return payment.status;
  const due = new Date(payment.dueDate);
  return due < reference ? "overdue" : "scheduled";
}

export async function getMySubscription(): Promise<MySubscription> {
  await randomDelay();
  return subscriptionByScenario[activeScenario];
}

export async function listMyPayments(params?: ListMyPaymentsParams): Promise<PagedPayments> {
  await randomDelay();
  const all = paymentsByScenario[activeScenario];
  const filtered = filterPayments(all, params?.status ?? "all");
  const page = Math.max(1, params?.page ?? 1);
  const pageSize = Math.min(50, Math.max(1, params?.pageSize ?? 10));
  const start = (page - 1) * pageSize;
  return {
    items: filtered.slice(start, start + pageSize),
    total: filtered.length,
    page,
    pageSize,
  };
}

function filterPayments(payments: MyPayment[], filter: PaymentStatusFilter): MyPayment[] {
  if (filter === "all") return payments;
  return payments.filter((p) => {
    const derived = deriveStatus(p);
    if (filter === "paid") return derived === "paid";
    if (filter === "refunded") return derived === "refunded" || derived === "chargeback";
    if (filter === "scheduled") return derived === "scheduled";
    if (filter === "overdue") return derived === "overdue";
    return false;
  });
}
